import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth.js";
import { LookupProvider } from "../context/LookupContext.js";
import { MeProvider } from "../context/MeContext.js";
import { LeaveTypeConfigProvider } from "../context/LeaveTypeConfigContext.js";
import { NotificationsProvider } from "../context/NotificationsContext.js";
import { AppShell } from "./AppShell.js";

// Deep link (e.g. /issues?highlight=<id> from an email) kept across sign-in. Password
// login lands on /dashboard and SSO on /, both inside AuthGuard, which sends the user on.
const RETURN_KEY = "hrms_return_to";

function isSafePath(p: string) {
  return p.startsWith("/") && !p.startsWith("//");
}

function rememberReturnPath(pathname: string, search: string) {
  if (pathname === "/" || pathname === "/dashboard") return;
  try { sessionStorage.setItem(RETURN_KEY, pathname + search); } catch { /* storage blocked */ }
}

function takeReturnPath(): string | null {
  try {
    const p = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return p && isSafePath(p) ? p : null;
  } catch { return null; }
}

function AuthLoading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <span>Loading…</span>
    </div>
  );
}

export function AuthGuard() {
  const { accessToken, isLoading } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // In an effect, not render: StrictMode renders twice and would lose the path.
  useEffect(() => {
    if (!accessToken) return;
    const back = takeReturnPath();
    if (back && back !== pathname + search) navigate(back, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  if (isLoading) return <AuthLoading />;
  if (!accessToken) {
    rememberReturnPath(pathname, search);
    return <Navigate to="/login" replace />;
  }
  return (
    <LookupProvider>
      <LeaveTypeConfigProvider>
        <MeProvider>
          <NotificationsProvider><AppShell /></NotificationsProvider>
        </MeProvider>
      </LeaveTypeConfigProvider>
    </LookupProvider>
  );
}

// Remembers the path but never consumes it: /setup-mfa lives here and must finish first.
export function AuthGuardFullPage() {
  const { accessToken, isLoading } = useAuth();
  const { pathname, search } = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!accessToken) {
    rememberReturnPath(pathname, search);
    return <Navigate to="/login" replace />;
  }
  return (
    <LookupProvider>
      <LeaveTypeConfigProvider>
        <MeProvider><Outlet /></MeProvider>
      </LeaveTypeConfigProvider>
    </LookupProvider>
  );
}
